import React from "react";
import { Drawer, Flex, Button, Tag } from "antd";
import { useSelector } from "react-redux";
import CreateBranchLocation from "./BranchLocation/CreateBranchLocation";
import CreateDepartment from "./Department/CreateDepartment";
import CreateCustomer from "./Customer/CreateCustomer";
import BranchSettingsS2 from "./steps/BranchSettingsS2";
import DimensionTypeHierarchy from "./steps/DimensionTypeHierarchy";
import TradeLicenseDrawer from "./TradeLicense/TradeLicenseDrawer";
import AssignEmployeesTab from "./Department/AssignEmployeesTab";

const DimensionDetailsDrawer = ({ open, onClose, type }) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const [currentStep, setCurrentStep] = React.useState(0);

  const s1Ref = React.useRef(null);
  const s2Ref = React.useRef(null);
  const s3Ref = React.useRef(null);
  const s4Ref = React.useRef(null);

  const normalizedType = (type || "").toLowerCase();
  const isTradeLicense =
    normalizedType.includes("trade license") ||
    normalizedType.includes("trade_license");
  const isBranchLocation =
    normalizedType.includes("branch location") ||
    normalizedType.includes("branch");
  const isDepartment = normalizedType.includes("department");
  const isCustomer =
    normalizedType.includes("customer") || normalizedType.includes("client");

  const getSteps = () => {
    if (isBranchLocation) {
      return [
        { key: 0, title: "GENERAL" },
        { key: 1, title: "BRANCH SETTINGS" },
        { key: 2, title: "ASSIGN EMPLOYEES" },
        { key: 3, title: "HIERARCHY" },
      ];
    }
    if (isDepartment) {
      return [
        { key: 0, title: "GENERAL" },
        { key: 1, title: "ASSIGN EMPLOYEES" },
        { key: 2, title: "HIERARCHY" },
      ];
    }
    if (isTradeLicense) {
      return [
        { key: 0, title: "GENERAL" },
        { key: 1, title: "ASSIGN EMPLOYEES" },
        { key: 2, title: "HIERARCHY" },
      ];
    }
    if (isCustomer) {
      return [
        { key: 0, title: "GENERAL" },
        { key: 1, title: "ASSIGN USERS" },
        { key: 2, title: "HIERARCHY" },
      ];
    }
    return [
      { key: 0, title: "GENERAL" },
      { key: 1, title: "ASSIGN EMPLOYEES" },
      { key: 2, title: "HIERARCHY" },
    ];
  };
  const steps = getSteps();

  const isHierarchyStep = () => {
    const currentStepData = steps.find((s) => s.key === currentStep);
    return currentStepData?.title === "HIERARCHY";
  };

  const StepTag = ({ step }) => {
    const isActive = currentStep >= step.key;
    const cardBorderColor =
      themeDefault?.info?.newConfig?.cardBorderColor || "#f0f0f0";
    const colorPrimary = themeDefault?.info?.colorPrimary || "#1890ff";
    const textColor = themeDefault?.info?.newConfig?.color;
    const radius = themeDefault?.info?.borderRadius;

    return (
      <Tag
        onClick={() => setCurrentStep(step.key)}
        style={{
          borderRadius: radius + "px",
          padding: "6px 10px",
          background: isActive ? colorPrimary + "30" : "transparent",
          color: textColor,
          cursor: "pointer",
          border: `1px solid ${isActive ? colorPrimary : cardBorderColor}`,
        }}
      >
        <Flex gap={8} align="center">
          <span
            style={{
              width: themeDefault?.info?.newConfig?.fontMediumSize,
              height: themeDefault?.info?.newConfig?.fontMediumSize,
              borderRadius: "50%",
              background: isActive ? colorPrimary : cardBorderColor,
              color: isActive
                ? themeDefault?.info?.newConfig?.colorTextSecondary
                : colorPrimary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            {step.key + 1}
          </span>
          {step.title}
        </Flex>
      </Tag>
    );
  };

  const renderStepContent = () => {
    if (currentStep === 0) {
      if (isBranchLocation) {
        return (
          <CreateBranchLocation
            ref={s1Ref}
            onClose={onClose}
            isCreateMode={false}
          />
        );
      }
      if (isTradeLicense) {
        return <TradeLicenseDrawer ref={s1Ref} isCreateMode={false} />;
      }
      if (isCustomer) {
        return (
          <CreateCustomer
            ref={s1Ref}
            onClose={onClose}
            open={open}
            isCreateMode={false}
          />
        );
      }
      return (
        <CreateDepartment ref={s1Ref} onClose={onClose} isCreateMode={false} />
      );
    }
    if (currentStep === 1) {
      if (isBranchLocation) return <BranchSettingsS2 ref={s2Ref} />;
      return <AssignEmployeesTab ref={s2Ref} />;
    }
    if (currentStep === 2) {
      if (isBranchLocation) return <AssignEmployeesTab ref={s3Ref} />;
      return <DimensionTypeHierarchy ref={s3Ref} dimensionType={type} />;
    }
    if (currentStep === 3) {
      if (isBranchLocation)
        return <DimensionTypeHierarchy ref={s4Ref} dimensionType={type} />;
    }
    return null;
  };

  const handleSave = async (mode) => {
    try {
      const refs = [s1Ref, s2Ref, s3Ref, s4Ref];
      const activeRef = refs[currentStep];
      let result;
      if (activeRef?.current?.submit) {
        result = await activeRef.current.submit();
        if (currentStep === 0 && !result) {
          if (mode === "in_progress" && currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
          }
          return;
        }
      } else {
        result = {};
      }

      const payload = {
        step: currentStep + 1,
        type,
        data: result || {},
        meta: { status: mode },
      };

      if (mode === "in_progress" && currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } catch (e) {
      console.error("Save failed:", e);
    }
  };

  return (
    <Drawer
      title={`${type || "Dimension"} / Details`}
      placement="right"
      open={open}
      onClose={onClose}
      width="100%"
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      footer={
        <Flex justify="space-between" align="center" style={{ width: "100%" }}>
          <Button onClick={onClose}>Cancel</Button>
          {!isHierarchyStep() && (
            <Flex gap={8}>
              {/* <Button onClick={() => handleSave("draft")}>Save as Draft</Button> */}
              <Button type="primary" onClick={() => handleSave("in_progress")}>
                Save and Continue
              </Button>
            </Flex>
          )}
        </Flex>
      }
    >
      {steps.length > 0 && (
        <Flex align="center" justify="space-between" style={{ padding: 16 }}>
          <Flex gap={8} wrap="wrap">
            {steps.map((s) => (
              <StepTag key={s.key} step={s} />
            ))}
          </Flex>
          {!isHierarchyStep() && (
            <Flex gap={8}>
              {/* <Button onClick={() => handleSave("draft")}>Save as Draft</Button> */}
              <Button type="primary" onClick={() => handleSave("in_progress")}>
                Save and Continue
              </Button>
            </Flex>
          )}
        </Flex>
      )}

      <div style={{ padding: 16 }}>{renderStepContent()}</div>
    </Drawer>
  );
};

export default DimensionDetailsDrawer;
